import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { searchCustomerApi } from "@/api/customer/customer";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import { searchStaffApi } from "@/api/staff";

const StaffMultiSelect = ({
  label = "Select Options",
  customerId = false,
  options = [],
  value = [],
  onChange,
}) => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const handleAdd = (item) => {
    if (!value.some((v) => v.id === item.id)) {
      onChange([item]); // single select
    }
    setSearch("");
    setIsOpen(false);
  };

  const handleRemove = (item) => {
    onChange(value.filter((i) => i.id !== item.id));
  };

  /* SEARCH API CALL (with debounce) */
  useEffect(() => {
    if (!search) {
      setSearchResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      setLoading(true);
      const res = await searchStaffApi(user?.restaurant_id, search);

      if (res?.status === "success") {
        setSearchResults(res.data.staff);
      } else {
        toast.error("Customer search failed");
      }
      setLoading(false);
    }, 400); // debounce

    return () => clearTimeout(timeout);
  }, [search]);

  const displayOptions = search ? searchResults : options;

  return (
    <div className="relative">
      {/* Input */}
      <div className="relative">
        <input
          type="text"
          value={search}
          placeholder="Search Customer"
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className={cn(
            "flex h-9 w-full text-cwhite3 rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base"
          )}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-2 text-gray-400"
        >
          ▼
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute w-full bg-gray-900 border border-gray-600 rounded mt-1 max-h-40 overflow-y-auto z-10">
          {loading ? (
            <div className="p-2 text-gray-400">Searching...</div>
          ) : displayOptions?.length > 0 ? (
            displayOptions?.map((item) => (
              <div
                key={item.id}
                className="p-2 hover:bg-gray-700 cursor-pointer"
                onClick={() => handleAdd(item)}
              >
                {customerId ? item.unique_id : item?.user.first_name + " " + item?.user.last_name}
              </div>
            ))
          ) : (
            <div className="p-2 text-gray-400">No customer found</div>
          )}
        </div>
      )}

      {/* Selected */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 border border-gray-600 rounded p-2">
          {value.map((item) => (
            <div
              key={item.id}
              className="flex items-center bg-gray-700 px-2 py-1 rounded"
            >
              <span>{customerId ? item.unique_id : item?.user.first_name + " " + item?.user.last_name}</span>
              <button
                className="ml-2 text-red-500"
                onClick={() => handleRemove(item)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffMultiSelect;
